import { createReducer } from 'reduxsauce';
import { AnyAction } from 'redux';
import { UserReadonly } from 'Models/Identity/User';
import ApiError from 'Models/Other/ApiError';
import PasswordReset from 'Models/Identity/PasswordReset';
import StateReadonly from './StateModel';
import { createScopedActions } from '.';
import UnlockUser from 'Models/Identity/UnlockUser';
import MfaGlobalConfig from 'Models/Identity/MfaGlobalConfig';
import MfaUserExtension from 'Models/Identity/MfaUserExtension';
import MfaDetailsResponse from 'Models/Identity/MfaDetailsResponse';
import MfaStatistics from 'Models/Identity/MfaStatistics';
import MfaTrustedDevice from 'Models/Identity/MfaTrustedDevice';

/* ------------- Interfaces for ReduxSauce ------------- */
interface IdentityState {
   user: UserReadonly;
   passwordResetResponse: PasswordReset;
   userUnlockResponse: PasswordReset;
   mfaResetResponse: PasswordReset;
   mfaConfig: MfaGlobalConfig | null;
   mfaDetails: MfaUserExtension | null;
   myMfaDetails: MfaDetailsResponse | null;
   mfaSmsEnrollmentResponse: PasswordReset;
   mfaAuthenticatorEnrollmentResponse: PasswordReset;
   mfaDefaultMethodResponse: PasswordReset;
   mfaStatistics: MfaStatistics | null;
   trustedDevice: MfaTrustedDevice | null;
   trustedDevices: readonly MfaTrustedDevice[] | null;
   revokeDeviceResponse: PasswordReset;
}

export type IdentityStateReadonly = Readonly<IdentityState>;

interface TypeNames {
   POST_PASSWORD_RESET_REQUEST: string;
   POST_PASSWORD_RESET_SUCCESS: string;
   POST_PASSWORD_RESET_FAILURE: string;
   PASSWORD_RESET_RESPONSE_RESET: string;
   POST_UNLOCK_USER_REQUEST: string;
   POST_UNLOCK_USER_SUCCESS: string;
   POST_UNLOCK_USER_FAILURE: string;
   UNLOCK_USER_RESPONSE_RESET: string;
   POST_MFA_RESET_REQUEST: string;
   POST_MFA_RESET_SUCCESS: string;
   POST_MFA_RESET_FAILURE: string;
   MFA_RESET_RESPONSE_RESET: string;
   GET_MFA_CONFIG_REQUEST: string;
   GET_MFA_CONFIG_SUCCESS: string;
   GET_MFA_CONFIG_FAILURE: string;
   GET_MFA_DETAILS_REQUEST: string;
   GET_MFA_DETAILS_SUCCESS: string;
   GET_MFA_DETAILS_FAILURE: string;
   GET_MY_MFA_DETAILS_REQUEST: string;
   GET_MY_MFA_DETAILS_SUCCESS: string;
   GET_MY_MFA_DETAILS_FAILURE: string;
   DELETE_MFA_SMS_ENROLLMENT_REQUEST: string;
   DELETE_MFA_SMS_ENROLLMENT_SUCCESS: string;
   DELETE_MFA_SMS_ENROLLMENT_FAILURE: string;
   MFA_SMS_ENROLLMENT_RESPONSE_RESET: string;
   DELETE_MFA_AUTHENTICATOR_ENROLLMENT_REQUEST: string;
   DELETE_MFA_AUTHENTICATOR_ENROLLMENT_SUCCESS: string;
   DELETE_MFA_AUTHENTICATOR_ENROLLMENT_FAILURE: string;
   MFA_AUTHENTICATOR_ENROLLMENT_RESPONSE_RESET: string;
   PATCH_MFA_DEFAULT_METHOD_REQUEST: string;
   PATCH_MFA_DEFAULT_METHOD_SUCCESS: string;
   PATCH_MFA_DEFAULT_METHOD_FAILURE: string;
   MFA_DEFAULT_METHOD_RESPONSE_RESET: string;
   GET_MFA_STATISTICS_REQUEST: string;
   GET_MFA_STATISTICS_SUCCESS: string;
   GET_MFA_STATISTICS_FAILURE: string;
   UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_REQUEST: string;
   UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_SUCCESS: string;
   UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_FAILURE: string;
   GET_TRUSTED_DEVICE_BY_ID_REQUEST: string;
   GET_TRUSTED_DEVICE_BY_ID_SUCCESS: string;
   GET_TRUSTED_DEVICE_BY_ID_FAILURE: string;
   GET_TRUSTED_DEVICES_REQUEST: string;
   GET_TRUSTED_DEVICES_SUCCESS: string;
   GET_TRUSTED_DEVICES_FAILURE: string;
   REVOKE_DEVICE_REQUEST: string;
   REVOKE_DEVICE_SUCCESS: string;
   REVOKE_DEVICE_FAILURE: string;
   REVOKE_DEVICE_RESPONSE_RESET: string;
}

type IdentityTypeNames = Readonly<TypeNames>;

export interface IdentityCreators {
   postPasswordResetRequest: (user: UserReadonly) => AnyAction;
   postPasswordResetSuccess: (
      passwordResetResponse: PasswordReset | undefined
   ) => AnyAction;
   postPasswordResetFailure: (
      error: ApiError,
      passwordResetResponse: PasswordReset | undefined
   ) => AnyAction;
   passwordResetResponseReset: () => AnyAction;
   postUnlockUserRequest: (userId: string) => AnyAction;
   postUnlockUserSuccess: (
      userUnlockResponse: UnlockUser | undefined
   ) => AnyAction;
   postUnlockUserFailure: (
      error: ApiError,
      userUnlockResponse: UnlockUser | undefined
   ) => AnyAction;
   unlockUserResponseReset: () => AnyAction;
   postMfaResetRequest: (userId: string) => AnyAction;
   postMfaResetSuccess: (
      mfaResetResponse: PasswordReset | undefined
   ) => AnyAction;
   postMfaResetFailure: (
      error: ApiError,
      mfaResetResponse: PasswordReset | undefined
   ) => AnyAction;
   mfaResetResponseReset: () => AnyAction;
   getMfaConfigRequest: () => AnyAction;
   getMfaConfigSuccess: (mfaConfig: MfaGlobalConfig) => AnyAction;
   getMfaConfigFailure: (error: ApiError) => AnyAction;
   getMfaDetailsRequest: (subjectId: string) => AnyAction;
   getMfaDetailsSuccess: (mfaDetails: MfaUserExtension) => AnyAction;
   getMfaDetailsFailure: (error: ApiError) => AnyAction;
   getMyMfaDetailsRequest: () => AnyAction;
   getMyMfaDetailsSuccess: (myMfaDetails: MfaDetailsResponse) => AnyAction;
   getMyMfaDetailsFailure: (error: ApiError) => AnyAction;
   deleteMfaSmsEnrollmentRequest: (subjectId: string) => AnyAction;
   deleteMfaSmsEnrollmentSuccess: (
      response: PasswordReset | undefined
   ) => AnyAction;
   deleteMfaSmsEnrollmentFailure: (
      error: ApiError,
      response: PasswordReset | undefined
   ) => AnyAction;
   mfaSmsEnrollmentResponseReset: () => AnyAction;
   deleteMfaAuthenticatorEnrollmentRequest: (subjectId: string) => AnyAction;
   deleteMfaAuthenticatorEnrollmentSuccess: (
      response: PasswordReset | undefined
   ) => AnyAction;
   deleteMfaAuthenticatorEnrollmentFailure: (
      error: ApiError,
      response: PasswordReset | undefined
   ) => AnyAction;
   mfaAuthenticatorEnrollmentResponseReset: () => AnyAction;
   patchMfaDefaultMethodRequest: (
      subjectId: string,
      newMethod: string
   ) => AnyAction;
   patchMfaDefaultMethodSuccess: (
      response: PasswordReset | undefined
   ) => AnyAction;
   patchMfaDefaultMethodFailure: (
      error: ApiError,
      response: PasswordReset | undefined
   ) => AnyAction;
   mfaDefaultMethodResponseReset: () => AnyAction;
   getMfaStatisticsRequest: () => AnyAction;
   getMfaStatisticsSuccess: (mfaStatistics: MfaStatistics) => AnyAction;
   getMfaStatisticsFailure: (error: ApiError) => AnyAction;
   updateMfaEnforcementDateAndMessageRequest: (
      enforcementDate: string | null,
      signinBannerMessage: string | null
   ) => AnyAction;
   updateMfaEnforcementDateAndMessageSuccess: (
      mfaConfig: MfaGlobalConfig
   ) => AnyAction;
   updateMfaEnforcementDateAndMessageFailure: (error: ApiError) => AnyAction;
   getTrustedDeviceByIdRequest: (id: string) => AnyAction;
   getTrustedDeviceByIdSuccess: (trustedDevice: MfaTrustedDevice) => AnyAction;
   getTrustedDeviceByIdFailure: (error: ApiError) => AnyAction;
   getTrustedDevicesRequest: () => AnyAction;
   getTrustedDevicesSuccess: (
      trustedDevices: readonly MfaTrustedDevice[]
   ) => AnyAction;
   getTrustedDevicesFailure: (error: ApiError) => AnyAction;
   revokeDeviceRequest: (id: string) => AnyAction;
   revokeDeviceSuccess: (response: PasswordReset | undefined) => AnyAction;
   revokeDeviceFailure: (
      error: ApiError,
      response: PasswordReset | undefined
   ) => AnyAction;
   revokeDeviceResponseReset: () => AnyAction;
}

/* ------------- Initial State ------------- */
export const INITIAL_STATE: IdentityStateReadonly = {
   user: ({} as unknown) as UserReadonly,
   passwordResetResponse: ({} as unknown) as PasswordReset,
   userUnlockResponse: ({} as unknown) as UnlockUser,
   mfaResetResponse: ({} as unknown) as PasswordReset,
   mfaConfig: null,
   mfaDetails: null,
   myMfaDetails: null,
   mfaSmsEnrollmentResponse: ({} as unknown) as PasswordReset,
   mfaAuthenticatorEnrollmentResponse: ({} as unknown) as PasswordReset,
   mfaDefaultMethodResponse: ({} as unknown) as PasswordReset,
   mfaStatistics: null,
   trustedDevice: null,
   trustedDevices: null,
   revokeDeviceResponse: ({} as unknown) as PasswordReset,
};

/* ------------- Reducers ------------- */
const postPasswordResetSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return {
         ...state,
         passwordResetResponse: action.response,
      };
   }
   return state;
};

const postPasswordResetFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      passwordResetResponse: action.response,
   };
};

const passwordResetResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      passwordResetResponse: INITIAL_STATE.passwordResetResponse,
   };
};

const postUnlockUserSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return {
         ...state,
         userUnlockResponse: action.response,
      };
   }
   return state;
};

const postUnlockUserFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      userUnlockResponse: action.response,
   };
};

const unlockUserResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      userUnlockResponse: INITIAL_STATE.userUnlockResponse,
   };
};

const postMfaResetSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return {
         ...state,
         mfaResetResponse: action.response,
      };
   }
   return state;
};

const postMfaResetFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      mfaResetResponse: action.response,
   };
};

const mfaResetResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaResetResponse: INITIAL_STATE.mfaResetResponse,
   };
};

const getMfaConfigSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      mfaConfig: action.mfaConfig,
   };
};

const getMfaConfigFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaConfig: null,
   };
};

const getMfaDetailsSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      mfaDetails: action.mfaDetails,
   };
};

const getMfaDetailsFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaDetails: null,
   };
};

const getMyMfaDetailsSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      myMfaDetails: action.myMfaDetails,
   };
};

const getMyMfaDetailsFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      myMfaDetails: null,
   };
};

const deleteMfaSmsEnrollmentSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return { ...state, mfaSmsEnrollmentResponse: action.response };
   }
   return state;
};

const deleteMfaSmsEnrollmentFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return { ...state, mfaSmsEnrollmentResponse: action.response };
};

const mfaSmsEnrollmentResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaSmsEnrollmentResponse: INITIAL_STATE.mfaSmsEnrollmentResponse,
   };
};

const deleteMfaAuthenticatorEnrollmentSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return { ...state, mfaAuthenticatorEnrollmentResponse: action.response };
   }
   return state;
};

const deleteMfaAuthenticatorEnrollmentFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return { ...state, mfaAuthenticatorEnrollmentResponse: action.response };
};

const mfaAuthenticatorEnrollmentResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaAuthenticatorEnrollmentResponse:
         INITIAL_STATE.mfaAuthenticatorEnrollmentResponse,
   };
};

const patchMfaDefaultMethodSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return { ...state, mfaDefaultMethodResponse: action.response };
   }
   return state;
};

const patchMfaDefaultMethodFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return { ...state, mfaDefaultMethodResponse: action.response };
};

const mfaDefaultMethodResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaDefaultMethodResponse: INITIAL_STATE.mfaDefaultMethodResponse,
   };
};

const getMfaStatisticsSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      mfaStatistics: action.mfaStatistics,
   };
};

const getMfaStatisticsFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      mfaStatistics: null,
   };
};

const updateMfaEnforcementDateAndMessageSuccess = getMfaConfigSuccess;

const updateMfaEnforcementDateAndMessageFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return state;
};

const getTrustedDeviceByIdSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      trustedDevice: action.trustedDevice,
   };
};

const getTrustedDeviceByIdFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      trustedDevice: null,
   };
};

const getTrustedDevicesSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return {
      ...state,
      trustedDevices: action.trustedDevices,
   };
};

const getTrustedDevicesFailure = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      trustedDevices: null,
   };
};

const revokeDeviceSuccess = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   if (action && action.response) {
      return { ...state, revokeDeviceResponse: action.response };
   }
   return state;
};

const revokeDeviceFailure = (
   state: IdentityStateReadonly,
   action: AnyAction
): IdentityStateReadonly => {
   return { ...state, revokeDeviceResponse: action.response };
};

const revokeDeviceResponseReset = (
   state: IdentityStateReadonly
): IdentityStateReadonly => {
   return {
      ...state,
      revokeDeviceResponse: INITIAL_STATE.revokeDeviceResponse,
   };
};

/* ------------- Types and Action Creators ------------- */
const { Types, Creators } = createScopedActions<
   IdentityTypeNames,
   IdentityCreators
>('identity', {
   postPasswordResetRequest: ['user'],
   postPasswordResetSuccess: ['response'],
   postPasswordResetFailure: ['error', 'response'],
   passwordResetResponseReset: [],
   postUnlockUserRequest: ['userId'],
   postUnlockUserSuccess: ['response'],
   postUnlockUserFailure: ['error', 'response'],
   unlockUserResponseReset: [],
   postMfaResetRequest: ['userId'],
   postMfaResetSuccess: ['response'],
   postMfaResetFailure: ['error', 'response'],
   mfaResetResponseReset: [],
   getMfaConfigRequest: [],
   getMfaConfigSuccess: ['mfaConfig'],
   getMfaConfigFailure: ['error'],
   getMfaDetailsRequest: ['subjectId'],
   getMfaDetailsSuccess: ['mfaDetails'],
   getMfaDetailsFailure: ['error'],
   getMyMfaDetailsRequest: [],
   getMyMfaDetailsSuccess: ['myMfaDetails'],
   getMyMfaDetailsFailure: ['error'],
   deleteMfaSmsEnrollmentRequest: ['subjectId'],
   deleteMfaSmsEnrollmentSuccess: ['response'],
   deleteMfaSmsEnrollmentFailure: ['error', 'response'],
   mfaSmsEnrollmentResponseReset: [],
   deleteMfaAuthenticatorEnrollmentRequest: ['subjectId'],
   deleteMfaAuthenticatorEnrollmentSuccess: ['response'],
   deleteMfaAuthenticatorEnrollmentFailure: ['error', 'response'],
   mfaAuthenticatorEnrollmentResponseReset: [],
   patchMfaDefaultMethodRequest: ['subjectId', 'newMethod'],
   patchMfaDefaultMethodSuccess: ['response'],
   patchMfaDefaultMethodFailure: ['error', 'response'],
   mfaDefaultMethodResponseReset: [],
   getMfaStatisticsRequest: [],
   getMfaStatisticsSuccess: ['mfaStatistics'],
   getMfaStatisticsFailure: ['error'],
   updateMfaEnforcementDateAndMessageRequest: [
      'enforcementDate',
      'signinBannerMessage',
   ],
   updateMfaEnforcementDateAndMessageSuccess: ['mfaConfig'],
   updateMfaEnforcementDateAndMessageFailure: ['error'],
   getTrustedDeviceByIdRequest: ['id'],
   getTrustedDeviceByIdSuccess: ['trustedDevice'],
   getTrustedDeviceByIdFailure: ['error'],
   getTrustedDevicesRequest: [],
   getTrustedDevicesSuccess: ['trustedDevices'],
   getTrustedDevicesFailure: ['error'],
   revokeDeviceRequest: ['id'],
   revokeDeviceSuccess: ['response'],
   revokeDeviceFailure: ['error', 'response'],
   revokeDeviceResponseReset: [],
});

export const IdentityTypes = Types;
export const IdentityActions = Creators;

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
   [Types.POST_PASSWORD_RESET_SUCCESS]: postPasswordResetSuccess,
   [Types.POST_PASSWORD_RESET_FAILURE]: postPasswordResetFailure,
   [Types.PASSWORD_RESET_RESPONSE_RESET]: passwordResetResponseReset,
   [Types.POST_UNLOCK_USER_SUCCESS]: postUnlockUserSuccess,
   [Types.POST_UNLOCK_USER_FAILURE]: postUnlockUserFailure,
   [Types.UNLOCK_USER_RESPONSE_RESET]: unlockUserResponseReset,
   [Types.POST_MFA_RESET_SUCCESS]: postMfaResetSuccess,
   [Types.POST_MFA_RESET_FAILURE]: postMfaResetFailure,
   [Types.MFA_RESET_RESPONSE_RESET]: mfaResetResponseReset,
   [Types.GET_MFA_CONFIG_SUCCESS]: getMfaConfigSuccess,
   [Types.GET_MFA_CONFIG_FAILURE]: getMfaConfigFailure,
   [Types.GET_MFA_DETAILS_SUCCESS]: getMfaDetailsSuccess,
   [Types.GET_MFA_DETAILS_FAILURE]: getMfaDetailsFailure,
   [Types.GET_MY_MFA_DETAILS_SUCCESS]: getMyMfaDetailsSuccess,
   [Types.GET_MY_MFA_DETAILS_FAILURE]: getMyMfaDetailsFailure,
   [Types.DELETE_MFA_SMS_ENROLLMENT_SUCCESS]: deleteMfaSmsEnrollmentSuccess,
   [Types.DELETE_MFA_SMS_ENROLLMENT_FAILURE]: deleteMfaSmsEnrollmentFailure,
   [Types.MFA_SMS_ENROLLMENT_RESPONSE_RESET]: mfaSmsEnrollmentResponseReset,
   [Types.DELETE_MFA_AUTHENTICATOR_ENROLLMENT_SUCCESS]: deleteMfaAuthenticatorEnrollmentSuccess,
   [Types.DELETE_MFA_AUTHENTICATOR_ENROLLMENT_FAILURE]: deleteMfaAuthenticatorEnrollmentFailure,
   [Types.MFA_AUTHENTICATOR_ENROLLMENT_RESPONSE_RESET]: mfaAuthenticatorEnrollmentResponseReset,
   [Types.PATCH_MFA_DEFAULT_METHOD_SUCCESS]: patchMfaDefaultMethodSuccess,
   [Types.PATCH_MFA_DEFAULT_METHOD_FAILURE]: patchMfaDefaultMethodFailure,
   [Types.MFA_DEFAULT_METHOD_RESPONSE_RESET]: mfaDefaultMethodResponseReset,
   [Types.GET_MFA_STATISTICS_SUCCESS]: getMfaStatisticsSuccess,
   [Types.GET_MFA_STATISTICS_FAILURE]: getMfaStatisticsFailure,
   [Types.UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_SUCCESS]: updateMfaEnforcementDateAndMessageSuccess,
   [Types.UPDATE_MFA_ENFORCEMENT_DATE_AND_MESSAGE_FAILURE]: updateMfaEnforcementDateAndMessageFailure,
   [Types.GET_TRUSTED_DEVICE_BY_ID_SUCCESS]: getTrustedDeviceByIdSuccess,
   [Types.GET_TRUSTED_DEVICE_BY_ID_FAILURE]: getTrustedDeviceByIdFailure,
   [Types.GET_TRUSTED_DEVICES_SUCCESS]: getTrustedDevicesSuccess,
   [Types.GET_TRUSTED_DEVICES_FAILURE]: getTrustedDevicesFailure,
   [Types.REVOKE_DEVICE_SUCCESS]: revokeDeviceSuccess,
   [Types.REVOKE_DEVICE_FAILURE]: revokeDeviceFailure,
   [Types.REVOKE_DEVICE_RESPONSE_RESET]: revokeDeviceResponseReset,
});

/* ------------- Selectors ------------- */

export const selectPasswordResetResponse = (
   state: StateReadonly
): PasswordReset => state.identity.passwordResetResponse;

export const selectUnlockUserResponse = (state: StateReadonly): UnlockUser =>
   state.identity.userUnlockResponse;

export const selectMfaResetResponse = (state: StateReadonly): PasswordReset =>
   state.identity.mfaResetResponse;

export const selectMfaConfig = (state: StateReadonly): MfaGlobalConfig | null =>
   state.identity.mfaConfig;

export const selectMfaDetails = (
   state: StateReadonly
): MfaUserExtension | null => state.identity.mfaDetails;

export const selectMyMfaDetails = (
   state: StateReadonly
): MfaDetailsResponse | null => state.identity.myMfaDetails;

export const selectMfaSmsEnrollmentResponse = (
   state: StateReadonly
): PasswordReset => state.identity.mfaSmsEnrollmentResponse;

export const selectMfaAuthenticatorEnrollmentResponse = (
   state: StateReadonly
): PasswordReset => state.identity.mfaAuthenticatorEnrollmentResponse;

export const selectMfaDefaultMethodResponse = (
   state: StateReadonly
): PasswordReset => state.identity.mfaDefaultMethodResponse;

export const selectMfaStatistics = (
   state: StateReadonly
): MfaStatistics | null => state.identity.mfaStatistics;

export const selectTrustedDevice = (
   state: StateReadonly
): MfaTrustedDevice | null => state.identity.trustedDevice;

export const selectTrustedDevices = (
   state: StateReadonly
): readonly MfaTrustedDevice[] | null => state.identity.trustedDevices;

export const selectRevokeDeviceResponse = (
   state: StateReadonly
): PasswordReset => state.identity.revokeDeviceResponse;
